import {Record, List} from 'immutable';
import User from 'records/User';
import {ADMIN_EDIT_USER_LANGUAGE_OPTION} from 'utils/const/adminEditUserFields';
import {DEFAULT_LANGUAGE} from 'utils/const/languageOptions';

/*
 * Construct Record(s) to encapsulate the server-side response into immutable objects for the front-end to use
 * Reference: https://execvision.myjetbrains.com/youtrack/issue/EV-15145#focus=Comments-112-107433.0-0
 */

interface UserPermissionsInterface {
  permissions: string[];
  departmentStatPermissions: string[];
  viewTeamPermissions: string[];
}
// warps around an edit user's permissions JSON object
export const UserPermissions = Record({
  permissions: [], // an array of permission strings
  departmentStatPermissions: [], // an array of DepartmentStatPermission
  viewTeamPermissions: [], // an array of ViewTeamPermission
});
export const DepartmentStatPermission = Record({
  departmentId: undefined,
});
export const ViewTeamPermission = Record({
  teamId: undefined,
});

// wraps around an editing user's integrations array
export const EditUserIntegration = Record({
  integrationId: undefined,
  externId: undefined,
});

// Used to wrap around a complete set of IntegrationUser metadata
export const UserIntegrationExtern = Record({
  id: undefined,
  name: undefined,
  label: undefined,
  externId: undefined,
  host: undefined,
  type: undefined,
});

/*
 Models the backend's GET response and PUT request. See the definition from
 https://execvision.atlassian.net/wiki/spaces/BIZ/pages/1435172882/Project+-+External+Admin+Backend+Kotlin
*/
export default class EditUser extends Record({
  userInfo: new User(),
  permissions: UserPermissions(),
  integrations: List(),
}) {
  readonly userInfo: User;
  readonly permissions: UserPermissionsInterface;
  readonly integrations: List<any>; // TODO better type for integrations.

  constructor(object = {} as any) {
    const userInConstructor = !!object.userInfo;

    let startingPoint: User | undefined;

    if (userInConstructor) {
      startingPoint = object.userInfo instanceof User ? object.userInfo : new User(object.userInfo);
    } else {
      startingPoint = new User({
        role: 'user',
        active: true,
        [ADMIN_EDIT_USER_LANGUAGE_OPTION]: [DEFAULT_LANGUAGE],
      });
    }

    // TODO Wrap UserPermissions.permissions, .departmentStatPermissions and viewTeamPermissions in List()
    super({
      userInfo: startingPoint,
      permissions: new UserPermissions({
        permissions: object.permissions ? object.permissions.permissions : [],
        departmentStatPermissions: object.permissions
          ? object.permissions.departmentStatPermissions.map(x => new DepartmentStatPermission(x))
          : [],
        viewTeamPermissions: object.permissions
          ? object.permissions.viewTeamPermissions.map(x => new ViewTeamPermission(x))
          : [],
      }),
      integrations: List(
        object.integrations
          ? object.integrations.map(x => {
              return EditUserIntegration(x);
            })
          : []
      ),
    });
  }
}

/**
 * Model all possible user permission strings in an immutable object to support AdminUserToggleField interactions
 * Reference: See javaapis/base/src/main/kotlin/io/execvision/model/UserPermission.kt for all the possible permission
 * string constants
 * @type {Record.Class}
 */
export const UserPermissionSet = Record({
  CAN_SHARE_LIBRARIES_GLOBALLY: undefined,
  CAN_SHARE_LIBRARIES_WITH_DEPARTMENTS: undefined,
  CAN_SHARE_LIBRARIES_WITH_TEAMS: undefined,
  CAN_SHARE_TRIGGERS_GLOBALLY: undefined,
  CAN_SHARE_TRIGGERS_WITH_DEPARTMENTS: undefined,
  CAN_SHARE_TRIGGERS_WITH_TEAMS: undefined,
  CAN_TWO_WAY_SYNC: undefined,
  CAN_VIEW_DEPT_STATS: undefined,
  CAN_VIEW_INSIGHTS_1: undefined,
  INCOGNITO: undefined,
  VIEW_ALL_ACCOUNT: undefined,
  VIEW_ALL_DEPARTMENT: undefined,
  VIEW_SELF: undefined,
  VIEW_TEAM: undefined,
});
export const USER_CAN_VIEW_DEPT_STATS = 'CAN_VIEW_DEPT_STATS';

/**
 * Model the dashboard mode a user can be assigned from the ext-admin page
 * @type {Record.Class}
 */
export const UserDashboardMode = Record({
  label: undefined,
  value: undefined,
});
export const USER_DASHBOARD_MODE_ROLE_LEVEL = 'roleLevel';
export const UserDashboardModeRoleLevelAdmin = new UserDashboardMode({
  label: 'All Departments View',
  value: USER_DASHBOARD_MODE_ROLE_LEVEL,
});
export const UserDashboardModeRoleLevelManager = new UserDashboardMode({
  label: 'Manager Dashboard',
  value: USER_DASHBOARD_MODE_ROLE_LEVEL,
});
export const USER_DASHBOARD_MODE_CAN_VIEW_DEPT_STATS = 'canViewDeptStats';
export const UserDashboardModeCanViewDeptStats = new UserDashboardMode({
  label: 'Select Departments View',
  value: USER_DASHBOARD_MODE_CAN_VIEW_DEPT_STATS,
});
